// @ts-nocheck
/**
 * Brackets — operator surface for the existing bracket engine.
 *
 * This panel is a caller only. Seeding order is previewed here and sent to
 * rle-generate-bracket as explicit seeds, so the bracket that gets built is
 * exactly the order the director saw. It never places a game: bracket games
 * are created unscheduled and are placed by "Place games" scoped to bracket.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Loader2, Trophy, Wand2, AlertTriangle, CheckCircle2, LayoutTemplate, ArrowRightCircle, Plus, Trash2, Download, ChevronDown, Pencil, Eye } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import BracketView from '@/components/staff/tournaments/BracketView';
import BracketPreviewOverlay from '@/components/admin/schedule/BracketPreviewOverlay';
import InlineNote from '@/components/admin/schedule/InlineNote';

interface Props {
  eventId: string;
  versionId: string | null | undefined;
  canManage: boolean;
  /** Limit the panel to one division (divisions-first navigation). */
  division?: string;
  /**
   * Every division that should get a row, even ones with no games at all.
   * A division with no bracket is the row a director most needs to see.
   */
  divisions?: string[];
  /** Called after a bracket is generated so the grid and counts refresh. */
  onChanged?: () => void;
  /** The page already prints "Brackets" in its header — do not print it twice. */
  hideHeading?: boolean;
}

type BracketRow = {
  id: string;
  name: string;
  format: string | null;
  /** The director's own words about this bracket — shown to the public. */
  description?: string | null;
  sortOrder?: number;
  games: number;
  scored: number;
  placed: number;
  completed: number;
};

type DivisionRow = {
  division: string;
  poolTotal: number;
  poolCompleted: number;
  bracketGames: number;
  bracketScored: number;
  bracketPlaced: number;
  bracketCompleted: number;
  /** A division can hold several brackets — Gold, Silver, Placement. */
  brackets: BracketRow[];
};

const BracketsPanel: React.FC<Props> = ({ eventId, versionId, canManage, division: only, divisions: knownDivisions, onChanged, hideHeading }) => {
  const [loading, setLoading] = useState(true);
  const [allRows, setRows] = useState<DivisionRow[]>([]);
  const rows = only ? allRows.filter((r) => r.division === only) : allRows;
  const [openDivision, setOpenDivision] = useState<string | null>(null);
  const [showDivision, setShowDivision] = useState<string | null>(null);
  const [showBracket, setShowBracket] = useState<{ id: string; name: string; division: string } | null>(null);
  // Preview: the bracket as a parent sees it. Read-only, full screen, Esc leaves.
  const [preview, setPreview] = useState<{ id: string; name: string; division: string } | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // Add a bracket to a division
  const [newFor, setNewFor] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newTemplate, setNewTemplate] = useState<string>('empty');
  const [newBusy, setNewBusy] = useState(false);
  const [newError, setNewError] = useState('');
  const [newTemplates, setNewTemplates] = useState<any[]>([]);
  // Brackets that can be built FROM another bracket: "the losers of round 1".
  const [newSources, setNewSources] = useState<any[]>([]);


  // Import a bracket built somewhere else
  const [impFor, setImpFor] = useState<string | null>(null);
  const [impScope, setImpScope] = useState<'here' | 'past'>('here');
  const [impHere, setImpHere] = useState<any[]>([]);
  const [impEvents, setImpEvents] = useState<any[]>([]);
  const [impEventId, setImpEventId] = useState<string>('');
  const [impEventBrackets, setImpEventBrackets] = useState<any[]>([]);
  const [impBracketId, setImpBracketId] = useState<string>('');
  const [impName, setImpName] = useState('');
  const [impPreview, setImpPreview] = useState<any>(null);
  const [impBusy, setImpBusy] = useState(false);
  const [impError, setImpError] = useState('');

  // Delete a bracket
  const [delBracket, setDelBracket] = useState<any>(null);
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState('');
  const [delPreview, setDelPreview] = useState<any>(null);

  // Rename a bracket, inline on its row
  const [renameFor, setRenameFor] = useState<string | null>(null);
  const [renameText, setRenameText] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);

  // Replace this bracket — both ways throw the current games away, so both warn first
  const [replaceWarn, setReplaceWarn] = useState<
    { bracket: BracketRow; division: string; how: 'regenerate' | 'template' } | null
  >(null);

  // Generate form
  const [qualifiers, setQualifiers] = useState<string>('all');
  const [thirdPlace, setThirdPlace] = useState(false);
  const [seedRows, setSeedRows] = useState<any[]>([]);
  const [seedLoading, setSeedLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Template library
  const [tplDivision, setTplDivision] = useState<string | null>(null);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplList, setTplList] = useState<any[]>([]);
  const [tplGaps, setTplGaps] = useState<any[]>([]);
  const [tplTeams, setTplTeams] = useState<number>(0);
  const [tplSlug, setTplSlug] = useState<string>('');
  const [tplPreview, setTplPreview] = useState<any>(null);
  const [tplBusy, setTplBusy] = useState(false);
  const [tplError, setTplError] = useState<string>('');
  // Replace the games in a bracket that is already there, or add a new one
  // beside it — the consolation set. Asked before anything is built.
  const [tplMode, setTplMode] = useState<'replace' | 'add'>('replace');
  const [tplBrackets, setTplBrackets] = useState<any[]>([]);
  const [tplTargetId, setTplTargetId] = useState<string>('');
  const [tplNewName, setTplNewName] = useState('');

  // Advance teams into an existing bracket
  const [advDivision, setAdvDivision] = useState<string | null>(null);
  const [advPreview, setAdvPreview] = useState<any>(null);
  const [advError, setAdvError] = useState<string>('');
  const [advBusy, setAdvBusy] = useState(false);
  const [advOverride, setAdvOverride] = useState(false);

  const previewAdvance = async (division: string, override: boolean) => {
    setAdvDivision(division);
    setAdvPreview(null); setAdvError(''); setAdvBusy(true);
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: {
        action: 'advance_teams', event_id: eventId, division,
        allow_incomplete_pool: override,
      },
    });
    setAdvBusy(false);
    if (error || data?.ok === false) {
      setAdvError(error?.message || data?.error?.message || 'Could not work out the seeding');
      return;
    }
    setAdvPreview(data.data);
  };

  const confirmAdvance = async () => {
    if (!advDivision) return;
    setAdvBusy(true); setAdvError('');
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: {
        action: 'advance_teams', event_id: eventId, division: advDivision,
        allow_incomplete_pool: advOverride, confirm: true,
      },
    });
    setAdvBusy(false);
    if (error || data?.ok === false) {
      setAdvError(error?.message || data?.error?.message || 'Could not seed the bracket');
      return;
    }
    toast.success(data.data?.message || 'Teams advanced.');
    setAdvDivision(null); setAdvPreview(null); setAdvOverride(false);
    setShowDivision(advDivision);
    setRefreshToken((t) => t + 1);
    await load();
    onChanged?.();
  };

  // Replacing a bracket has to cover the division exactly. Adding one takes a
  // subset by definition, so every shape up to the division's size is offered.
  const tplFitting = useMemo(
    () => (tplBrackets.length && tplMode === 'add'
      ? tplList.filter((t: any) => t.can_add ?? Number(t.team_count) <= tplTeams)
      : tplList.filter((t: any) => t.team_count === tplTeams)),
    [tplList, tplMode, tplTeams, tplBrackets.length],
  );

  /**
   * `preferBracketId` is the bracket whose row was clicked. Replace has to
   * default to that one — defaulting to the first bracket in the division
   * means opening this from the Consolation row and pressing Replace wipes
   * the main bracket.
   */
  const openTemplates = async (division: string, preferBracketId?: string) => {
    setTplDivision(division);
    setTplList([]); setTplGaps([]); setTplSlug(''); setTplPreview(null); setTplError('');
    setTplBrackets([]); setTplTargetId(''); setTplNewName(''); setTplMode('replace');
    setTplLoading(true);
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'bracket_templates_list', event_id: eventId, division },
    });
    setTplLoading(false);
    if (error || data?.ok === false) {
      setTplError(error?.message || data?.error?.message || 'Could not load the template library');
      return;
    }
    setTplList(data.data?.templates ?? []);
    setTplGaps(data.data?.gaps ?? []);
    setTplTeams(data.data?.teams_in_division ?? 0);
    const brs = data.data?.brackets ?? [];
    setTplBrackets(brs);
    setTplTargetId(
      (preferBracketId && brs.some((b: any) => b.id === preferBracketId) ? preferBracketId : brs[0]?.id) ?? '',
    );
    // With a bracket already there, adding is the safe default — replacing
    // throws games away, so it should be the deliberate choice.
    setTplMode(brs.length ? 'add' : 'replace');
  };

  /** The body both the preview and the build send — one place, so they agree. */
  const tplBody = (slug: string, confirm: boolean) => ({
    action: 'bracket_apply',
    event_id: eventId,
    division: tplDivision,
    template_slug: slug,
    mode: tplBrackets.length ? tplMode : 'replace',
    bracket_id: tplMode === 'replace' && tplTargetId ? tplTargetId : undefined,
    new_bracket_name: tplMode === 'add' ? tplNewName.trim() : undefined,
    ...(confirm ? { confirm: true, discard_scores: true } : {}),
  });

  const previewTemplate = async (slug: string) => {
    if (!tplDivision) return;
    setTplSlug(slug); setTplPreview(null); setTplError('');
    setTplBusy(true);
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: tplBody(slug, false),
    });
    setTplBusy(false);
    if (error || data?.ok === false) {
      setTplError(error?.message || data?.error?.message || 'Could not use that template');
      return;
    }
    setTplPreview(data.data);
  };

  const applyTemplate = async () => {
    if (!tplDivision || !tplSlug) return;
    setTplBusy(true); setTplError('');
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: tplBody(tplSlug, true),
    });
    setTplBusy(false);
    if (error || data?.ok === false) {
      setTplError(error?.message || data?.error?.message || 'Could not build that bracket');
      return;
    }
    toast.success(data.data?.message || 'Bracket built.');
    setTplDivision(null);
    setShowDivision(tplDivision);
    setRefreshToken((t) => t + 1);
    await load();
    onChanged?.();
  };

  /** Adding a bracket: a name the director types, and a template or nothing. */
  const openNewBracket = async (division: string) => {
    setNewFor(division); setNewName(''); setNewTemplate('empty'); setNewError('');
    setNewTemplates([]); setNewSources([]);
    const { data } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'bracket_templates_list', event_id: eventId, division },
    });
    if (data?.ok) {
      setNewTemplates(data.data?.templates ?? []);
      setNewSources(data.data?.consolation_sources ?? []);
    }
  };

  const createBracket = async () => {
    if (!newFor) return;
    setNewBusy(true); setNewError('');
    // "from:<bracket id>:<rule>" — built from another bracket, not a shape.
    const from = newTemplate.startsWith('from:') ? newTemplate.slice(5).split(':') : null;
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: {
        action: 'bracket_create', event_id: eventId, division: newFor, name: newName.trim(),
        template_slug: from || newTemplate === 'empty' ? null : newTemplate,
        from_bracket_id: from ? from[0] : undefined,
        from_rule: from ? from[1] : undefined,
      },
    });
    setNewBusy(false);

    if (error || data?.ok === false) {
      setNewError(error?.message || data?.error?.message || 'Could not add that bracket');
      return;
    }
    toast.success(data.data?.message || 'Bracket added.');
    setNewFor(null);
    setShowDivision(newFor);
    setRefreshToken((t) => t + 1);
    await load();
    onChanged?.();
  };

  /**
   * Import: the source is a real bracket, not a library shape. Nothing is
   * written until the director has seen the preview and pressed Import.
   */
  const openImport = async (division: string) => {
    console.info('[Brackets] Import trigger fired', { division });
    setImpFor(division); setImpScope('here'); setImpBracketId(''); setImpEventId('');
    setImpEventBrackets([]); setImpName(''); setImpPreview(null); setImpError(''); setImpBusy(true);
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'bracket_import_sources', event_id: eventId },
    });
    setImpBusy(false);
    if (error || data?.ok === false) {
      setImpError(error?.message || data?.error?.message || 'Could not load what you can import from');
      return;
    }
    setImpHere(data.data?.here ?? []);
    setImpEvents(data.data?.events ?? []);
  };

  const loadPastEventBrackets = async (evId: string) => {
    setImpEventId(evId); setImpBracketId(''); setImpPreview(null); setImpError(''); setImpBusy(true);
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'bracket_import_sources', event_id: eventId, source_event_id: evId },
    });
    setImpBusy(false);
    if (error || data?.ok === false) {
      setImpError(error?.message || data?.error?.message || 'Could not read that event');
      return;
    }
    setImpEventBrackets(data.data?.brackets ?? []);
  };

  const previewImport = async () => {
    if (!impFor || !impBracketId) return;
    setImpBusy(true); setImpError(''); setImpPreview(null);
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: {
        action: 'bracket_import_preview', event_id: eventId,
        division: impFor, source_bracket_id: impBracketId,
      },
    });
    setImpBusy(false);
    if (error || data?.ok === false) {
      setImpError(error?.message || data?.error?.message || 'Could not read that bracket');
      return;
    }
    setImpPreview(data.data);
    if (!impName.trim() && data.data?.source?.bracket) setImpName(String(data.data.source.bracket));
  };

  /**
   * The write. It reports itself either way: a green toast naming what was
   * created, or a red toast plus an inline reason. It never closes the dialog
   * on failure, and it never ends without saying something — a silent import
   * is indistinguishable from a broken feature.
   */
  const runImport = async () => {
    const division = impFor;
    if (!division || !impBracketId) {
      const message = 'Pick the bracket to import first.';
      setImpError(message);
      toast.error(`Not imported. ${message}`);
      return;
    }
    if (!impName.trim()) {
      const message = 'Give the imported bracket a name.';
      setImpError(message);
      toast.error(`Not imported. ${message}`);
      return;
    }
    const toastId = toast.loading(`Importing ${impPreview?.games ?? ''} games into ${division}…`);
    setImpBusy(true); setImpError('');
    try {
      const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: {
          action: 'bracket_import', event_id: eventId, division,
          source_bracket_id: impBracketId, name: impName.trim(), confirm: true,
        },
      });

      if (error) throw new Error(error.message || 'The import did not reach the server.');
      if (!data) throw new Error('The import did not reach the server — nothing was created.');
      if (data.ok === false) throw new Error(data.error?.message || 'The import was refused.');
      if (data.data?.imported !== true) {
        throw new Error(
          data.data?.requires_confirmation
            ? 'The server did not treat this as confirmed, so nothing was written.'
            : 'The server reported no bracket was created.',
        );
      }

      toast.success(data.data.message || `${impName.trim()} imported into ${division}.`, { id: toastId });
      setImpFor(null); setImpPreview(null);
      setShowDivision(division);
      setRefreshToken((t) => t + 1);
      await load();
      onChanged?.();
    } catch (e: any) {
      const message = e?.message || 'That bracket was not imported.';
      setImpError(message);
      toast.error(`Not imported. ${message}`, { id: toastId });
    } finally {
      setImpBusy(false);
    }
  };

  const askDeleteBracket = async (bracket: any, division: string) => {
    console.info('[Brackets] Delete trigger fired', { division, bracketId: bracket.id });
    setDelBracket({ ...bracket, division }); setDelError(''); setDelPreview(null); setDelBusy(true);
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'bracket_delete', event_id: eventId, bracket_id: bracket.id },
    });
    setDelBusy(false);
    if (error || data?.ok === false) {
      setDelError(error?.message || data?.error?.message || 'That bracket cannot be deleted');
      return;
    }
    setDelPreview(data.data);
  };

  const confirmDeleteBracket = async () => {
    if (!delBracket) return;
    setDelBusy(true); setDelError('');
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'bracket_delete', event_id: eventId, bracket_id: delBracket.id, confirm: true },
    });
    setDelBusy(false);
    if (error || data?.ok === false) {
      setDelError(error?.message || data?.error?.message || 'That bracket cannot be deleted');
      return;
    }
    toast.success(data.data?.message || 'Bracket deleted.');
    setDelBracket(null); setDelPreview(null);
    setRefreshToken((t) => t + 1);
    await load();
    onChanged?.();
  };

  const load = useCallback(async () => {
    if (!versionId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    const [{ data: games }, { data: bracketList }] = await Promise.all([
      supabase
        .from('scheduled_games')
        .select('id, division, game_type, game_status, court_id, scheduled_date, score_a, score_b, bracket_id')
        .eq('schedule_version_id', versionId),
      supabase
        .from('event_brackets')
        .select('id, division, name, format, sort_order, description')
        .eq('schedule_version_id', versionId),
    ]);

    const byDiv = new Map<string, DivisionRow>();
    const ensure = (d: string) => {
      if (!byDiv.has(d)) {
        byDiv.set(d, {
          division: d, poolTotal: 0, poolCompleted: 0,
          bracketGames: 0, bracketScored: 0, bracketPlaced: 0, bracketCompleted: 0,
          brackets: [],
        });
      }
      return byDiv.get(d)!;
    };

    for (const b of bracketList || []) {
      ensure(b.division || 'Other').brackets.push({
        id: b.id, name: b.name, format: b.format ?? null, description: b.description ?? null,
        sortOrder: Number(b.sort_order ?? 0),
        games: 0, scored: 0, placed: 0, completed: 0,
      });
    }

    for (const g of games || []) {
      const d = g.division || 'Other';
      const row = ensure(d);
      if (g.game_type === 'pool') {
        row.poolTotal += 1;
        if (g.game_status === 'completed') row.poolCompleted += 1;
      } else if (g.game_type === 'bracket' || g.game_type === 'consolation') {
        row.bracketGames += 1;
        if (g.score_a != null || g.score_b != null) row.bracketScored += 1;
        if (g.court_id || g.scheduled_date) row.bracketPlaced += 1;
        if (g.game_status === 'completed') row.bracketCompleted += 1;
        const b = row.brackets.find((x) => x.id === g.bracket_id);
        if (b) {
          b.games += 1;
          if (g.score_a != null || g.score_b != null) b.scored += 1;
          if (g.court_id || g.scheduled_date) b.placed += 1;
          if (g.game_status === 'completed') b.completed += 1;
        }
      }
    }
    // The order a director set, not the alphabet: brackets list by the order
    // they were created in, with the name only as a tie-break.
    for (const row of byDiv.values()) {
      row.brackets.sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
      );
    }

    // A division with no games at all still gets a row, saying it has no
    // bracket yet — otherwise it would simply be missing from the page.
    for (const d of knownDivisions ?? []) if (d) ensure(String(d));

    const list = Array.from(byDiv.values()).sort((a, b) => a.division.localeCompare(b.division));
    setRows(list);
    setShowDivision((cur) => {
      const withBracket = list.filter((r) => r.bracketGames > 0).map((r) => r.division);
      if (cur && withBracket.includes(cur)) return cur;
      return withBracket[0] ?? null;
    });
    const allBrackets = list.flatMap((r) => r.brackets.map((b) => ({ ...b, division: r.division })));
    setShowBracket((cur) => {
      const still = cur ? allBrackets.find((b) => b.id === cur.id) : null;
      if (still) return { id: still.id, name: still.name, division: still.division };
      const first = allBrackets.find((b) => b.games > 0) ?? allBrackets[0];
      return first ? { id: first.id, name: first.name, division: first.division } : null;
    });
    setLoading(false);
  }, [versionId, (knownDivisions ?? []).join('|')]);

  useEffect(() => { void load(); }, [load]);

  /** Seed preview comes from the same shared engine used by standings and places. */
  const buildSeeds = useCallback(async (division: string) => {
    if (!versionId) return;
    setSeedLoading(true);
    try {
      const { data: response, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'standings_data', event_id: eventId, division },
      });
      if (error || !response?.ok) {
        throw new Error(error?.message || response?.error?.message || 'Could not calculate seeds');
      }
      const rows = response.data?.rows ?? [];
      const unresolved = rows.filter((row: any) => row.tied && row.place == null);
      if (unresolved.length) {
        const names = unresolved.map((row: any) => row.team_name).join(', ');
        toast.error(`Set division places for the unresolved tie before generating: ${names}.`);
        setSeedRows([]);
        return;
      }
      const out = rows
        .map((row: any) => ({
          registration_id: row.registration_id,
          name: row.team_name,
          pool: row.pool_name || '',
          pool_rank: row.pool_rank ?? 99,
          wins: row.wins ?? 0,
          losses: row.losses ?? 0,
          ties: row.ties ?? 0,
          diff: row.diff ?? 0,
          points_for: row.points_for ?? 0,
          played: (row.games_played ?? 0) > 0,
          seed_place: row.place ?? row.computed_place,
        }))
        .sort((a: any, b: any) => a.seed_place - b.seed_place);
      setSeedRows(out);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not calculate seeds');
      setSeedRows([]);
    } finally {
      setSeedLoading(false);
    }
  }, [eventId, versionId]);


  const openGenerate = (division: string) => {
    setOpenDivision(division);
    setQualifiers('all');
    setThirdPlace(false);
    setAcknowledged(false);
    setSeedRows([]);
    void buildSeeds(division);
  };

  const current = rows.find((r) => r.division === openDivision) || null;
  const qualified = useMemo(() => {
    if (qualifiers === 'all') return seedRows;
    const n = Number(qualifiers);
    return seedRows.filter((s) => s.pool_rank <= n);
  }, [seedRows, qualifiers]);

  const unplayed = current ? current.poolTotal - current.poolCompleted : 0;
  const hasBracket = (current?.bracketGames ?? 0) > 0;
  const needsAck = unplayed > 0 || hasBracket;

  const generate = async () => {
    if (!versionId || !openDivision) return;
    if (qualified.length < 2) {
      toast.error('Need at least 2 qualifying teams.');
      return;
    }
    setGenerating(true);
    const { data, error } = await invokeEdgeFunction('rle-generate-bracket', {
      body: {
        schedule_version_id: versionId,
        division: openDivision,
        source: 'manual',
        seeds: qualified.map((s) => ({ registration_id: s.registration_id })),
        third_place: thirdPlace,
        replace: true,
      },
    });
    setGenerating(false);
    if (error || data?.ok === false) {
      toast.error(error?.message || data?.error?.message || 'Could not generate the bracket');
      return;
    }
    const created = data?.data?.summary?.games_created;
    toast.success(`${openDivision} bracket created${created ? ` — ${created} games` : ''}.`);
    setOpenDivision(null);
    setShowDivision(openDivision);
    setRefreshToken((t) => t + 1);
    await load();
    onChanged?.();
  };

  /** Save an inline bracket rename. Blank or unchanged just closes the editor. */
  /** The bracket's description: his words, saved where they are typed. */
  const saveBracketDescription = async (bracketId: string, next: string) => {
    const { error } = await (supabase as any)
      .from('event_brackets').update({ description: next || null }).eq('id', bracketId);
    if (error) { toast.error('That description was not saved.'); return false; }
    setRows((prev) => prev.map((r) => ({
      ...r,
      brackets: r.brackets.map((b) => (b.id === bracketId ? { ...b, description: next || null } : b)),
    })));
    toast.success(next ? 'Description saved.' : 'Description cleared.');
    return true;
  };

  const saveRename = async (b: BracketRow) => {
    const name = renameText.trim();
    if (!name || name === b.name) { setRenameFor(null); return; }
    setRenameBusy(true);
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'bracket_rename', event_id: eventId, bracket_id: b.id, name },
    });
    setRenameBusy(false);
    if (error || data?.ok === false) {
      toast.error(error?.message || data?.error?.message || 'Could not rename the bracket');
      return;
    }
    toast.success(data?.data?.message || `That bracket is now ${name}.`);
    setRenameFor(null);
    setShowBracket((cur) => (cur && cur.id === b.id ? { ...cur, name } : cur));
    setRefreshToken((t) => t + 1);
    await load();
    onChanged?.();
  };

  if (!versionId) return null;

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        {!hideHeading && (
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-base font-semibold">Brackets</h2>
          </div>
        )}
        <p className="text-sm text-muted-foreground">
          Each division is its own tournament. Bracket games are created unscheduled — run
          <strong> Place games</strong> scoped to <strong>Bracket only</strong> to give them courts and times.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading brackets…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No games yet.</p>
        ) : (
          <ul className="space-y-5">
            {rows.map((r) => (
              <li key={r.division} className="space-y-4 rounded-lg border bg-muted/50 p-4" data-bracket-division={r.division}>
                {/* The division is a heading with its state and one button. */}
                <div className="flex flex-wrap items-center gap-3 border-b pb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-base font-semibold tracking-tight">
                      {r.division}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                      {r.poolTotal === 0 ? (
                        'No pool games'
                      ) : r.poolCompleted === r.poolTotal ? (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                          Pool play complete ({r.poolTotal} of {r.poolTotal} played)
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                          Pool play incomplete — {r.poolTotal - r.poolCompleted} of {r.poolTotal} pool
                          games in {r.division} are still unplayed
                        </>
                      )}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-stretch">
                      <Button
                        size="sm"
                        className="rounded-r-none"
                        onClick={() => void openNewBracket(r.division)}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add a bracket
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" className="rounded-l-none border-l border-primary-foreground/25 px-2"
                            aria-label={`More ways to add a bracket to ${r.division}`}>
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => void openImport(r.division)}>
                            <Download className="mr-2 h-4 w-4" />
                            Import a bracket
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => void openTemplates(r.division)}>
                            <LayoutTemplate className="mr-2 h-4 w-4" />
                            Use a template
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>

                {/* Each bracket is an indented card under its division. */}
                {r.brackets.length > 0 ? (
                  <ul className="space-y-4 pl-4 sm:pl-6">
                    {r.brackets.map((b) => (
                      <li key={b.id} className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-4 py-3 shadow-md" data-bracket-card={b.id}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            {renameFor === b.id ? (
                              <Input
                                autoFocus
                                className="h-7 w-48 text-sm"
                                value={renameText}
                                disabled={renameBusy}
                                onChange={(e) => setRenameText(e.target.value)}
                                onBlur={() => void saveRename(b)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') void saveRename(b);
                                  if (e.key === 'Escape') setRenameFor(null);
                                }}
                              />
                            ) : canManage ? (
                              <button
                                type="button"
                                className="flex items-center gap-1.5 rounded px-1 -ml-1 text-left hover:bg-muted"
                                title="Click to rename this bracket"
                                onClick={() => { setRenameText(b.name); setRenameFor(b.id); }}
                              >
                                {b.name}
                                <Pencil className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                              </button>
                            ) : (
                              b.name
                            )}
                            <Badge variant={b.games ? 'secondary' : 'outline'} className="text-[10px]">
                              {b.games ? `${b.games} game${b.games === 1 ? '' : 's'}` : 'Empty'}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {b.games
                              ? `${b.placed} placed · ${b.completed} played`
                              : 'No games in it yet — add them by hand or use a template.'}
                          </div>
                          {/* His own description of this bracket — public-facing. */}
                          <div className="group mt-1 max-w-xl">
                            <InlineNote
                              value={b.description}
                              canEdit={canManage}
                              limit={500}
                              multiline
                              addLabel="+ Add a description"
                              editTitle="Edit this bracket's description"
                              onSave={(next) => saveBracketDescription(b.id, next)}
                            />
                          </div>
                        </div>
                        {b.games > 0 && (
                          <>
                            {/* "View" viewed nothing — it selected this bracket
                                below. It now says what it does. */}
                            <Button size="sm" variant="ghost"
                              onClick={() => { setShowDivision(r.division); setShowBracket({ id: b.id, name: b.name, division: r.division }); }}>
                              Open
                            </Button>
                            <Button size="sm" variant="outline"
                              onClick={() => setPreview({ id: b.id, name: b.name, division: r.division })}>
                              <Eye className="mr-2 h-4 w-4" />
                              Preview
                            </Button>
                          </>
                        )}
                        {canManage && b.games > 0 && (
                          <Button size="sm" variant="outline"
                            onClick={() => { setAdvOverride(false); void previewAdvance(r.division, false); }}>
                            <ArrowRightCircle className="mr-2 h-4 w-4" />
                            Advance teams
                          </Button>
                        )}
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="outline">
                                <Wand2 className="mr-2 h-4 w-4" />
                                Replace this bracket
                                <ChevronDown className="ml-1.5 h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  b.games
                                    ? setReplaceWarn({ bracket: b, division: r.division, how: 'regenerate' })
                                    : openGenerate(r.division)
                                }
                              >
                                <Wand2 className="mr-2 h-4 w-4" />
                                Regenerate
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  b.games
                                    ? setReplaceWarn({ bracket: b, division: r.division, how: 'template' })
                                    : void openTemplates(r.division, b.id)
                                }
                              >
                                <LayoutTemplate className="mr-2 h-4 w-4" />
                                Use a template
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {canManage && (
                          <Button size="sm" variant="ghost" className="text-destructive"
                            onClick={() => void askDeleteBracket(b, r.division)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="rounded-md border border-dashed bg-card px-4 py-6 text-sm text-muted-foreground sm:ml-6">
                    No bracket yet.
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Regenerate and Use a template both throw the current games away. Say so first. */}
        <Dialog open={!!replaceWarn} onOpenChange={(o) => { if (!o) setReplaceWarn(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Replace {replaceWarn?.bracket.name}?</DialogTitle>
              <DialogDescription>
                {replaceWarn
                  ? `${replaceWarn.how === 'regenerate' ? 'Regenerating' : 'Using a template'} builds `
                    + `${replaceWarn.bracket.name} again from scratch. Its `
                    + `${replaceWarn.bracket.games} game${replaceWarn.bracket.games === 1 ? '' : 's'} `
                    + `in ${replaceWarn.division}${
                        replaceWarn.bracket.completed
                          ? `, including ${replaceWarn.bracket.completed} already played,`
                          : ''
                      } will be discarded.`
                  : ''}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReplaceWarn(null)}>Keep this bracket</Button>
              <Button
                onClick={() => {
                  const w = replaceWarn;
                  setReplaceWarn(null);
                  if (!w) return;
                  if (w.how === 'regenerate') openGenerate(w.division);
                  else void openTemplates(w.division, w.bracket.id);
                }}
              >
                {replaceWarn?.how === 'regenerate' ? 'Regenerate' : 'Use a template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {preview && (
          <BracketPreviewOverlay
            open
            onClose={() => setPreview(null)}
            eventId={eventId}
            versionId={versionId}
            division={preview.division}
            bracketId={preview.id}
            bracketName={preview.name}
          />
        )}

        {showBracket && rows.some((r) => r.brackets.some((b) => b.id === showBracket.id)) && (
          <div className="space-y-2 border-t pt-4">
            <div className="flex flex-wrap items-center gap-2">
              {rows.flatMap((r) => r.brackets.map((b) => (
                <Button
                  key={b.id}
                  size="sm"
                  variant={b.id === showBracket.id ? 'default' : 'outline'}
                  onClick={() => setShowBracket({ id: b.id, name: b.name, division: r.division })}
                >
                  {r.division} · {b.name}
                </Button>
              )))}
            </div>
            <BracketView
              eventId={eventId}
              canManage={false}
              hideActions
              versionId={versionId}
              division={showBracket.division}
              bracketId={showBracket.id}
              bracketName={showBracket.name}
              refreshToken={refreshToken}
              editable={canManage}
              onChanged={() => { void load(); onChanged?.(); }}
            />
          </div>
        )}
      </CardContent>

      <Dialog open={!!openDivision} onOpenChange={(o) => !o && setOpenDivision(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate {openDivision} bracket</DialogTitle>
            <DialogDescription>
              Seeds come from pool results. Nothing is written until you generate.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm">Teams that qualify</Label>
              <Select value={qualifiers} onValueChange={setQualifiers}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All pool teams</SelectItem>
                  <SelectItem value="1">Top 1 per pool</SelectItem>
                  <SelectItem value="2">Top 2 per pool</SelectItem>
                  <SelectItem value="3">Top 3 per pool</SelectItem>
                  <SelectItem value="4">Top 4 per pool</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox id="third" checked={thirdPlace}
                onCheckedChange={(v) => setThirdPlace(v === true)} />
              <Label htmlFor="third" className="text-sm font-normal">
                Add a third-place game
              </Label>
            </div>

            <div className="space-y-1">
              <Label className="text-sm">
                Seed preview — {qualified.length} team{qualified.length === 1 ? '' : 's'}
              </Label>
              {seedLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Working out seeds…
                </div>
              ) : qualified.length === 0 ? (
                <p className="py-3 text-sm text-muted-foreground">
                  No pooled teams found in this division.
                </p>
              ) : (
                <div className="max-h-56 overflow-y-auto rounded border">
                  {qualified.map((s, i) => (
                    <div key={s.registration_id}
                      className="flex items-center gap-2 border-b px-3 py-1.5 text-sm last:border-b-0">
                      <span className="w-6 font-mono text-xs text-muted-foreground">{i + 1}</span>
                      <span className="min-w-0 flex-1 truncate">{s.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {s.pool} #{s.pool_rank} · {s.wins}-{s.losses}
                        {s.ties ? `-${s.ties}` : ''}
                        {s.played ? '' : ' (no games played)'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {unplayed > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <div className="flex items-center gap-2 font-semibold">
                  <AlertTriangle className="h-4 w-4" />
                  {unplayed} of {current?.poolTotal} pool games in {openDivision} are still unplayed.
                </div>
                <p className="mt-1">
                  Seeding now builds the bracket on a partial table. Records shown above are the
                  results so far.
                </p>
              </div>
            )}

            {hasBracket && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <div className="flex items-center gap-2 font-semibold text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  {current!.bracketCompleted > 0
                    ? `${current!.bracketCompleted} ${openDivision} bracket game${current!.bracketCompleted === 1 ? ' has' : 's have'} already been played.`
                    : `This replaces the existing ${openDivision} bracket.`}
                </div>
                <p className="mt-1">
                  Regenerating deletes {current!.bracketGames} bracket game
                  {current!.bracketGames === 1 ? '' : 's'} — {current!.bracketScored} with score
                  {current!.bracketScored === 1 ? '' : 's'}, {current!.bracketPlaced} already placed
                  on a court and time. Matchups, scores and placements on those games cannot be
                  recovered: saved arrangements restore placements only, never matchups.
                </p>
              </div>
            )}

            {needsAck && (
              <div className="flex items-start gap-2">
                <Checkbox id="ack" checked={acknowledged}
                  onCheckedChange={(v) => setAcknowledged(v === true)} />
                <Label htmlFor="ack" className="text-sm font-normal">
                  I understand{unplayed > 0 ? ' pool play is incomplete' : ''}
                  {unplayed > 0 && hasBracket ? ' and' : ''}
                  {hasBracket ? ' the existing bracket will be destroyed' : ''}. Generate anyway.
                </Label>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDivision(null)} disabled={generating}>
              Cancel
            </Button>
            <Button
              onClick={() => void generate()}
              disabled={generating || seedLoading || qualified.length < 2 || (needsAck && !acknowledged)}
            >
              {generating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {hasBracket ? 'Replace bracket' : 'Generate bracket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tplDivision} onOpenChange={(o) => !o && setTplDivision(null)}>
        {/* A column: the header stays, the middle scrolls, the buttons are
            pinned to the bottom of the card. On a short laptop window the
            confirmation used to push the build button off the screen. */}
        <DialogContent className="flex max-h-[85vh] max-w-lg flex-col gap-4 overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle>Ready-made brackets for {tplDivision}</DialogTitle>
            <DialogDescription>
              {tplTeams} team{tplTeams === 1 ? '' : 's'} in this division. Pick a shape — the games are
              created empty, with no teams, courts or times, and nothing is written until you confirm.
            </DialogDescription>
          </DialogHeader>

          {tplLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
              {tplBrackets.length > 0 && (
                <div className="space-y-2 rounded-lg border p-3">
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="radio" className="mt-1" name="tpl-mode" checked={tplMode === 'add'}
                      onChange={() => { setTplMode('add'); setTplSlug(''); setTplPreview(null); setTplError(''); }}
                    />
                    <span>
                      <span className="font-medium">Add this as a new bracket</span>
                      <span className="block text-xs text-muted-foreground">
                        A second bracket beside {tplBrackets.map((b: any) => b.name).join(', ')} — a consolation
                        or placement set. Nothing already there is touched, and every slot arrives empty for you
                        to wire (Loser of Game 18).
                      </span>
                    </span>
                  </label>
                  {tplMode === 'add' && (
                    <Input
                      value={tplNewName}
                      onChange={(e) => { setTplNewName(e.target.value); setTplPreview(null); }}
                      placeholder="Name this bracket — Consolation, Placement"
                      className="h-8 text-sm"
                    />
                  )}

                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="radio" className="mt-1" name="tpl-mode" checked={tplMode === 'replace'}
                      onChange={() => { setTplMode('replace'); setTplSlug(''); setTplPreview(null); setTplError(''); }}
                    />
                    <span>
                      <span className="font-medium">
                        Replace the games in {tplBrackets.find((b: any) => b.id === tplTargetId)?.name
                          ?? tplBrackets[0]?.name}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        The games in that bracket are deleted and the new shape is built in their place.
                      </span>
                    </span>
                  </label>
                  {tplMode === 'replace' && tplBrackets.length > 1 && (
                    <Select
                      value={tplTargetId}
                      onValueChange={(v) => { setTplTargetId(v); setTplPreview(null); }}
                    >
                      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Which bracket" /></SelectTrigger>
                      <SelectContent>
                        {tplBrackets.map((b: any) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.name} · {b.games} game{b.games === 1 ? '' : 's'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                {tplFitting.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {tplMode === 'add'
                      ? `No ready-made bracket small enough for ${tplDivision} yet.`
                      : <>No ready-made bracket for {tplTeams} teams yet. Use <strong>Generate bracket</strong>{' '}
                        instead, or change how many teams are in this division.</>}
                  </p>
                ) : (
                  tplFitting.map((t: any) => (
                    <button
                      key={t.slug}
                      type="button"
                      onClick={() => void previewTemplate(t.slug)}
                      className={`w-full rounded border px-3 py-2 text-left text-sm hover:bg-muted ${
                        tplSlug === t.slug ? 'border-primary bg-muted' : ''
                      }`}
                    >
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {t.game_count} game{t.game_count === 1 ? '' : 's'}{t.description ? ` · ${t.description}` : ''}
                      </div>
                    </button>
                  ))
                )}
              </div>

              {tplMode === 'replace' && tplGaps.length > 0 && (
                <ul className="space-y-0.5 text-xs text-muted-foreground">
                  {tplGaps
                    .filter((g: any) => g.team_count === tplTeams)
                    .map((g: any, i: number) => <li key={i}>{g.message}</li>)}
                </ul>
              )}

              {tplBusy && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Checking…
                </div>
              )}

              {tplError && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                  {tplError}
                </div>
              )}

              {tplPreview && (
                <div className="rounded-lg border bg-muted/40 p-3 text-sm">
                  {tplPreview.message}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="shrink-0 border-t pt-3">
            <Button variant="outline" onClick={() => setTplDivision(null)} disabled={tplBusy}>
              Cancel
            </Button>
            <Button
              onClick={() => void applyTemplate()}
              disabled={tplBusy || !tplPreview
                || (tplBrackets.length > 0 && tplMode === 'add' && !tplNewName.trim())}
            >
              {tplBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tplBrackets.length > 0 && tplMode === 'add' ? 'Add this bracket' : 'Build this bracket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!advDivision} onOpenChange={(o) => { if (!o) { setAdvDivision(null); setAdvPreview(null); setAdvError(''); setAdvOverride(false); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Advance teams into the {advDivision} bracket</DialogTitle>
            <DialogDescription>
              The order comes from the division standings and any places you set by hand.
              Nothing is written until you confirm.
            </DialogDescription>
          </DialogHeader>

          {advBusy && !advPreview ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Working out the seeding…
            </div>
          ) : advError ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {advError}
              </div>
              {!advOverride && /pool games/i.test(advError) && (
                <Button size="sm" variant="outline"
                  onClick={() => { setAdvOverride(true); void previewAdvance(advDivision as string, true); }}>
                  Seed anyway, with pool play unfinished
                </Button>
              )}
            </div>
          ) : advPreview ? (
            <div className="space-y-3">
              <div className="rounded-lg border bg-muted/40 p-3 text-sm">{advPreview.message}</div>
              <div className="max-h-72 overflow-y-auto rounded border">
                {(advPreview.locking_in ?? []).map((s: any, i: number) => (
                  <div key={`in-${i}`}
                    className="flex items-center gap-2 border-b px-3 py-1.5 text-sm last:border-b-0">
                    <span className="w-24 shrink-0 truncate font-mono text-xs text-muted-foreground">{s.game}</span>
                    <span className="min-w-0 flex-1 truncate">{s.slot} → {s.team_name}</span>
                    <Badge className="text-[10px]">Locking in</Badge>
                  </div>
                ))}
                {(advPreview.leaving_alone ?? []).map((s: any, i: number) => (
                  <div key={`out-${i}`}
                    className="flex items-start gap-2 border-b px-3 py-1.5 text-sm last:border-b-0">
                    <span className="w-24 shrink-0 truncate font-mono text-xs text-muted-foreground">{s.game}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{s.slot}</span>
                      {s.reason && <span className="block text-xs text-muted-foreground">{s.reason}</span>}
                    </span>
                    <Badge variant="secondary" className="text-[10px]">Left alone</Badge>
                  </div>
                ))}
              </div>

            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setAdvDivision(null); setAdvPreview(null); }} disabled={advBusy}>
              Cancel
            </Button>
            <Button onClick={() => void confirmAdvance()} disabled={advBusy || !advPreview}>
              {advBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Put these teams in the bracket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add a bracket to a division */}
      <Dialog open={!!newFor} onOpenChange={(o) => !o && setNewFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add a bracket to {newFor}</DialogTitle>
            <DialogDescription>
              A division can run more than one — a Gold and a Silver, a championship and a
              placement bracket. Each keeps its own rounds and its own wiring.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input value={newName} placeholder="Gold" onChange={(e) => setNewName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Build it from</Label>
              <Select value={newTemplate} onValueChange={setNewTemplate}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="empty">Build it empty — add games by hand</SelectItem>
                  {newSources.map((s: any) => (
                    <SelectItem key={`${s.from_bracket_id}:${s.rule}`} value={`from:${s.from_bracket_id}:${s.rule}`}>
                      {s.name} · {s.teams} teams
                    </SelectItem>
                  ))}
                  {newTemplates.map((t: any) => (
                    <SelectItem key={t.slug} value={t.slug}>
                      {t.name} · {t.team_count} teams
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* What the chosen source will actually build, before anything is written. */}
            {newTemplate.startsWith('from:') && (() => {
              const s = newSources.find((x: any) => `from:${x.from_bracket_id}:${x.rule}` === newTemplate);
              return s ? (
                <p className="text-xs text-muted-foreground" data-consolation-preview>{s.description}</p>
              ) : null;
            })()}
            {newError && <p className="text-sm text-destructive">{newError}</p>}

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFor(null)} disabled={newBusy}>Cancel</Button>
            <Button onClick={() => void createBracket()} disabled={newBusy || !newName.trim()}>
              {newBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add bracket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import a bracket someone already built — another division, or another event.
          The content is a column: header and footer stay put, only the middle scrolls. */}
      {impFor && (
      <Dialog
        key="bracket-import"
        open
        onOpenChange={(o) => {
          if (!o) {
            setImpFor(null); setImpPreview(null); setImpError('');
          }
        }}
      >
        <DialogContent
          className="flex max-h-[85vh] max-w-lg flex-col gap-4 overflow-hidden"
          onOpenAutoFocus={() => console.info('[Brackets] Import dialog mounted', { division: impFor })}
        >
          <DialogHeader>
            <DialogTitle>Import a bracket into {impFor}</DialogTitle>
            <DialogDescription>
              Copies the shape — rounds and the wiring between games. Teams, dates, times, courts
              and results never come across. This always creates a new bracket.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">

            <div className="flex gap-2">
              <Button size="sm" variant={impScope === 'here' ? 'default' : 'outline'}
                onClick={() => { setImpScope('here'); setImpBracketId(''); setImpPreview(null); }}>
                Another division here
              </Button>
              <Button size="sm" variant={impScope === 'past' ? 'default' : 'outline'}
                onClick={() => { setImpScope('past'); setImpBracketId(''); setImpPreview(null); }}>
                Another event
              </Button>
            </div>

            {impScope === 'past' && (
              <div className="space-y-1">
                <Label className="text-xs">Event</Label>
                <Select value={impEventId} onValueChange={(v) => void loadPastEventBrackets(v)}>
                  <SelectTrigger><SelectValue placeholder="Pick an event you run" /></SelectTrigger>
                  <SelectContent>
                    {impEvents.map((e: any) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name}{e.start_date ? ` · ${e.start_date}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {impEvents.length === 0 && !impBusy && (
                  <p className="text-xs text-muted-foreground">No other events you can administer.</p>
                )}
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs">Bracket to copy</Label>
              <Select value={impBracketId} onValueChange={(v) => { setImpBracketId(v); setImpPreview(null); }}>
                <SelectTrigger><SelectValue placeholder="Pick a bracket" /></SelectTrigger>
                <SelectContent>
                  {(impScope === 'here' ? impHere : impEventBrackets).map((b: any) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.division} · {b.name} ({b.games} games)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Name it in {impFor}</Label>
              <Input value={impName} onChange={(e) => setImpName(e.target.value)} placeholder="Championship" />
            </div>

            {impPreview && (
              <div className="space-y-2 rounded border bg-muted/30 p-3 text-sm">
                <p>
                  {impPreview.games} game{impPreview.games === 1 ? '' : 's'} across {impPreview.rounds} round
                  {impPreview.rounds === 1 ? '' : 's'} will be created in {impFor}.
                </p>
                <p className="text-xs text-muted-foreground">{impPreview.note}</p>
                {impPreview.unset?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium">
                      {impPreview.unset.length} slot{impPreview.unset.length === 1 ? '' : 's'} will arrive unset:
                    </p>
                    <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                      {impPreview.unset.map((u: any, i: number) => (
                        <li key={i}>{u.game} — side {u.side} was {u.was}; {u.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {impPreview.warnings?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-amber-700">Worth a look:</p>
                    <ul className="mt-1 space-y-0.5 text-xs text-amber-700">
                      {impPreview.warnings.map((w: any, i: number) => (
                        <li key={i}>{w.game} — side {w.side} asks for {w.was}, but {w.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {impError && <p className="text-sm text-destructive">{impError}</p>}
          </div>

          <DialogFooter className="shrink-0 border-t pt-3">

            <Button variant="ghost" onClick={() => setImpFor(null)}>Cancel</Button>
            {!impPreview ? (
              <Button disabled={!impBracketId || impBusy} onClick={() => void previewImport()}>
                {impBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Preview
              </Button>
            ) : (
              <Button
                disabled={!impName.trim() || impBusy}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  void runImport();
                }}
              >
                {impBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import {impPreview.games} games
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Delete a bracket — its own games only, and never with a score on them */}
      {delBracket && (
      <Dialog
        key="bracket-delete"
        open
        onOpenChange={(o) => {
          if (!o) {
            setDelBracket(null); setDelPreview(null);
          }
        }}
      >
        <DialogContent
          className="max-w-md"
          onOpenAutoFocus={() => console.info('[Brackets] Delete dialog mounted', { bracketId: delBracket?.id })}
        >
          <DialogHeader>
            <DialogTitle>Delete {delBracket?.name}?</DialogTitle>
            <DialogDescription>
              {delPreview?.message
                || 'Only this bracket and its own games are affected. Nothing in any other bracket is touched.'}
            </DialogDescription>
          </DialogHeader>
          {delError && <p className="text-sm text-destructive">{delError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDelBracket(null); setDelPreview(null); }} disabled={delBusy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void confirmDeleteBracket()} disabled={delBusy || !delPreview}>
              {delBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete bracket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}
    </Card>
  );
};

export default BracketsPanel;
